import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SitesService } from '../sites.service';
import { Site } from '../site';
import { Location } from '@angular/common';
import { SiteChart } from '../site-chart';
import { SiteTotalService } from '../site-total.service';
import { forkJoin } from 'rxjs';
import { ThemeService } from '../theme.service';

@Component({
  selector: 'app-sites',
  templateUrl: './sites.component.html',
  styleUrls: ['./sites.component.scss']
})
export class SitesComponent implements OnInit, OnDestroy {

  siteItems: Site[] = [];
  siteChart: SiteChart[] = [];
  branch = '';
  // numCols: any = 3;
  currDate: Date = new Date();
  refreshTimer: any;
  // rowHeight: any = 400;

  // sites will contain a key of a site and it'll hold the value of assignments
  sites: Record<number, any[]> = {};
  // key: site ID, value: inspector IDs
  futureSiteInspectors: Record<number, number[]> = {};
  // key: site ID, value: positions
  positions: Record<number, any[]> = {};

  constructor(
    private route: ActivatedRoute,
    private sitesService: SitesService,
    private sitesTotalService: SiteTotalService,
    public themeService: ThemeService,
    private location: Location,
    private router: Router
  ) {
    // this.route.params.subscribe(event => {
    //   this.ngOnInit();
    // });
  }

  ngOnInit() {
    if (localStorage.getItem('token') === null) {
      this.router.navigate(['/login']);
    }
    if (localStorage.getItem('selectedDate') !== null) {
      this.currDate = new Date(localStorage.getItem('selectedDate'));
    } else {
      localStorage.setItem('selectedDate', this.getFormattedDate(this.currDate));
    }
    localStorage.removeItem('country');
    localStorage.removeItem('siteId');
    // this.adjustSize();

    this.branch = this.route.snapshot.paramMap.get('branch');
    if (this.branch) {
      this.branch = this.branch;
      localStorage.setItem('branchCode', this.branch);
    } else {
      this.branch = 'ALL';
    }

    this.getSitesForBranch(this.branch);
    this.getSitesAssignments();
    this.refreshTimer = setInterval(() => {
      this.getSitesForBranch(this.branch);
      this.getSitesAssignments();
    }, 1000 * 60 * 10);
  }

  ngOnDestroy() {
    clearInterval(this.refreshTimer);
  }

  getSitesAssignments(): void {
    // Clear data
    this.sites = {};
    this.futureSiteInspectors = {};
    this.positions = {};

    const selectedDate = new Date(this.currDate);
    selectedDate.setDate(selectedDate.getDate() + 1);

    const currentDate = this.getFormattedDate(this.currDate);
    const nextDayDate = this.getFormattedDate(selectedDate);

    // forkJoin is like Promise.all, but it's used in Angular instead
    forkJoin([
      this.sitesTotalService.getSitesAssignments(currentDate),
      this.sitesTotalService.getSitesPositions(currentDate),
      this.sitesTotalService.getFutureInspectorIds(nextDayDate)
    ]).subscribe(([currentAssignments, currentPositions, futureInspectors]) => { // Each param index will represent API responses respectively

      const localFutureInspectors: Record<number, number[]> = {};

      futureInspectors.forEach(assignment => {
        // Fill futureSiteInspectors key with the assignment site ID and fill the value with its inspector IDs
        localFutureInspectors[assignment.site] = assignment.inspectorIds.split(',').map(inspectorId => Number(inspectorId));
      });

      this.futureSiteInspectors = localFutureInspectors;

      const localAssignments: Record<number, any[]> = {};

      // The goal of this loop is to loop through each assignment and group them by their site ID
      currentAssignments.forEach(assignment => {
        // If key exists then push the data
        if (localAssignments[assignment.site]) {
          localAssignments[assignment.site].push(assignment);
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          localAssignments[assignment.site] = [assignment];
        }
      });

      this.sites = localAssignments;

      const localPositions: Record<number, any[]> = {};

      // Same process as the top currentAssignments
      currentPositions.forEach(position => {
        // If key exists then push the data
        if (localPositions[position.site]) {
          localPositions[position.site].push(position);
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          localPositions[position.site] = [position];
        }
      });

      this.positions = localPositions;
    });
  }

  getSitesForBranch( branchCode: string ): void {
    this.sitesService.getSitesForBranch(this.branch, this.getFormattedDate(this.currDate))
      // .subscribe(siteHolder => this.siteItems = siteHolder);
      .subscribe((data) => {
        this.compileSiteData(data);
      });
  }

  changeDate( t: any ): void {
    this.currDate = t.value;
    localStorage.setItem('selectedDate', t.value);
    this.getSitesForBranch(this.branch);
    this.getSitesAssignments();
  }

  getFormattedDate( dt: Date ): string {
    let m: any = dt.getMonth() + 1;
    m = (m < 10) ? '0' + m.toString() : m.toString();
    let d: any = dt.getDate();
    d = (d < 10) ? '0' + d.toString() : d.toString();

    return dt.getFullYear().toString() + '-' + m.toString() + '-' + d.toString();
  }

  compileSiteData( data: any ) {
    this.siteChart = [];
    let tempName = '';
    let tempSiteChart: SiteChart = new SiteChart;
    let hitCount = 0;
    let tempTotal = 0;
    let tempFilled = 0;

    for (const item of data) {
      if (tempName === '') {
        hitCount = 1;
        tempName = item.siteCustomerName;
        tempSiteChart.siteCustomerName = item.siteCustomerName;
        tempSiteChart.branch = item.branch;
        tempSiteChart.s1Label = item.shift;
        tempSiteChart.s1qtyFilled = item.qtyFilled;
        tempSiteChart.s1qtyUnfilled = item.qtyUnfilled;
        tempSiteChart.site = item.site;
        tempTotal += parseFloat(item.qtyFilled) + parseFloat(item.qtyUnfilled);
        tempFilled += parseFloat(item.qtyFilled);
      } else {
        if (item.siteCustomerName === tempName) {
          if (hitCount === 1) {
            hitCount++;
            tempSiteChart.s2Label = item.shift;
            tempSiteChart.s2qtyFilled = item.qtyFilled;
            tempSiteChart.s2qtyUnfilled = item.qtyUnfilled;
            tempTotal += parseFloat(item.qtyFilled) + parseFloat(item.qtyUnfilled);
            tempFilled += parseFloat(item.qtyFilled);
          } else if (hitCount === 2) {
            hitCount++;
            tempSiteChart.s3Label = item.shift;
            tempSiteChart.s3qtyFilled = item.qtyFilled;
            tempSiteChart.s3qtyUnfilled = item.qtyUnfilled;
            tempTotal += parseFloat(item.qtyFilled) + parseFloat(item.qtyUnfilled);
            tempFilled += parseFloat(item.qtyFilled);
          } else if (hitCount === 3) {
            hitCount++;
            tempSiteChart.s4Label = item.shift;
            tempSiteChart.s4qtyFilled = item.qtyFilled;
            tempSiteChart.s4qtyUnfilled = item.qtyUnfilled;
            tempTotal += parseFloat(item.qtyFilled) + parseFloat(item.qtyUnfilled);
            tempFilled += parseFloat(item.qtyFilled);
          } else if (hitCount === 4) {
            hitCount++;
            tempSiteChart.s5Label = item.shift;
            tempSiteChart.s5qtyFilled = item.qtyFilled;
            tempSiteChart.s5qtyUnfilled = item.qtyUnfilled;
            tempTotal += parseFloat(item.qtyFilled) + parseFloat(item.qtyUnfilled);
            tempFilled += parseFloat(item.qtyFilled);
          } else if (hitCount === 5) {
            hitCount++;
            tempSiteChart.s6Label = item.shift;
            tempSiteChart.s6qtyFilled = item.qtyFilled;
            tempSiteChart.s6qtyUnfilled = item.qtyUnfilled;
            tempTotal += parseFloat(item.qtyFilled) + parseFloat(item.qtyUnfilled);
            tempFilled += parseFloat(item.qtyFilled);
          }
        } else {
          tempSiteChart.total = tempTotal;
          tempSiteChart.filled = tempFilled;
          this.siteChart.push(tempSiteChart);
          tempSiteChart = new SiteChart;
          tempTotal = 0;
          tempFilled = 0;
          hitCount = 1;
          tempName = item.siteCustomerName;
          tempSiteChart.siteCustomerName = item.siteCustomerName;
          tempSiteChart.branch = item.branch;
          tempSiteChart.s1Label = item.shift;
          tempSiteChart.s1qtyFilled = item.qtyFilled;
          tempSiteChart.s1qtyUnfilled = item.qtyUnfilled;
          tempSiteChart.site = item.site;
          tempTotal += parseFloat(item.qtyFilled) + parseFloat(item.qtyUnfilled);
          tempFilled += parseFloat(item.qtyFilled);
        }
      }
    }
    this.siteItems = data;
    tempSiteChart.total = tempTotal;
    tempSiteChart.filled = tempFilled;
    this.siteChart.push(tempSiteChart);
    this.sortSites();
  }

  goBack(): void {
    this.location.back();
  }

  // onResize( event ) {
  //   const element = event.target.innerWidth;
  //   if (element < 535) {
  //     this.rowHeight = '325px;';
  //     this.numCols = 1;
  //   } else if (element < 1100) {
  //     this.numCols = 1;
  //     this.rowHeight = '500px;';
  //   } else if (element < 1600) {
  //     this.numCols = 2;
  //   } else {
  //     this.numCols = 3;
  //   }
  // }

  // adjustSize(  ) {
  //   const element = window.innerWidth;
  //   if (element < 535) {
  //     this.rowHeight = '325px;';
  //     this.numCols = 1;
  //   } else if (element < 1100) {
  //     this.rowHeight = '500px;';
  //     this.numCols = 1;
  //   } else if (element < 1600) {
  //     this.numCols = 2;
  //   } else {
  //     this.numCols = 3;
  //   }
  // }

  goSite( site: string ) {
    if (site.indexOf('/') > -1) {
      site = site.replace(/\//g, '#2F');
      this.router.navigate(['shifts/' + site]);
    } else {
      this.router.navigate(['shifts/' + site]);
    }
  }

  filter( data: any[] ): any[] {
    const sc: SiteChart[] = [];
    const searchArr = (<HTMLInputElement>document.getElementById('search')).value.trim().split(' ');
    data.forEach(function(d) {
      let hit = 0;
      const itemDetails = d.siteCustomerName.trim();
      searchArr.forEach(searchTerm => {
        if (itemDetails.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1) {
          hit++;
        }
      });
      if (hit === searchArr.length) {
        sc.push(d);
      }
    });
    return sc;
  }

  sortSites() {
    this.siteChart.sort((a, b) => {
      if (a.total === 0 && b.total === 0) { // Both totals are 0, so seperate sort for alpha
        if (a.siteCustomerName < b.siteCustomerName) {
          return -1;
        } else {
          return 1;
        }
      } else if (a.total === 0) { // Only a is 0, send to end
        return 1;
      } else if (b.total === 0) { // Only b is 0, send to end
        return -1;
      } else { // Alpha sort, both items have values
        if (a.siteCustomerName < b.siteCustomerName) {
          return -1;
        } else {
          return 1;
        }
      }
    });
  }

}
